// @givmo/employer-module — how the sign-up funnel LOOKS in each host (EP-MNT8; one register since
// EP-WEB43).
//
// ══ ONE REGISTER: THE HOST'S OWN SIGN-IN DOOR ══════════════════════════════════════════════════
//
// Each host draws the funnel in the register of its own sign-in door, and the two doors share one:
// the employer PORTAL's chooser (`apps/admin`'s `SignInMainScreen`) and the company CONSOLE's
// `SignInPage` both set strong headings, the SaaS button, the branded provider buttons and
// brand-coloured text links. A composed door that opens onto scaffolding interiors is the "register
// cliff" the house design rules name, and on a door where a company starts moving money it reads as
// a credential-harvest site. Until EP-WEB43 the portal's funnel still wore the Flutter port's blue
// headings, round pill and underlined links beside its redesigned chooser (Jonathan, 2026-10-19:
// "the design isn't consistent - not professional").
//
// So the classes are one set (`DOOR_REGISTER`), the structure is one (each heading held with its
// explanation, every step's Back at the top of its card, a primary action before the text controls
// beside it), and a host's look differs only where its own sign-in door does: how tight its ways in
// stack (`choiceGroup`), whether its chooser splits the work-email way from the providers with a
// divider (`choiceDivider`), and where the Terms sentence sits (`terms`). The controls and their test
// ids are the same in both hosts, and so are the words — but for the few that name the HOST itself
// (`SignUpWords`): its sign-in, its sign-out, what an approved teammate opens and who approves them.
// The portal says "Log-in", "Logout", "the portal" and "an owner"; the console says what its own door
// and shell say — and never "Owner" for the company-wide role, which every console string calls
// "Global Admin" (`@givmo/org-console`'s `org-tiers.ts`).

import type { ButtonProps } from '@givmo/design-system';
import { GLOBAL_ADMIN_LABEL } from '@givmo/org-console';

/** The props that make a design-system `Button` a screen's primary action. */
export type SignUpPrimaryButton = Pick<ButtonProps, 'appearance' | 'tone' | 'size' | 'fullWidth'>;

/** Every class the funnel's screens take from their host. */
export interface SignUpLook {
  /** The screen's heading. */
  readonly heading: string;
  /** A heading that reports a finished step ("Verified!", "Request sent"). */
  readonly successHeading: string;
  /** A paragraph explaining the screen. */
  readonly body: string;
  /** A screen's ways forward (the chooser's three registrations), stacked as the door stacks its own. */
  readonly choiceGroup: string;
  /**
   * Whether the chooser splits its work-email way from the provider ways with the divider the host's
   * sign-in chooser draws between its own ("or continue with"). The console's sign-up stacks its
   * three ways as one set, as ruled (D-EP-60 (1)).
   */
  readonly choiceDivider: boolean;
  /** A text control that leaves the screen: Back, Logout. */
  readonly textButton: string;
  /** The code screen's resend control, live or cooling down. */
  readonly resendButton: (enabled: boolean) => string;
  /** The link inside a sentence ("Log-in"). */
  readonly inlineLink: string;
  /** The screen's primary action. */
  readonly primary: SignUpPrimaryButton;
  /** A live password rule, met or not. */
  readonly passwordRule: (met: boolean) => string;
  /** The company's name beside its logo on the request-access screen. */
  readonly companyName: string;
  /** The Terms / Privacy sentence, placed as the host's sign-in door places its last line. */
  readonly terms: string;
}

/** The doors' text control — the classes the console's sign-in gives "Forgot your password?". */
const TEXT_BUTTON =
  'self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/** The quiet type of the Terms sentence, wherever a host sets it. */
const TERMS_TYPE = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';

/** The register both hosts' sign-in doors are drawn in. */
const DOOR_REGISTER = {
  // Balanced: a two-line heading at phone width never leaves its last word alone ("…already on /
  // Givmo").
  heading: 'font-sans text-s22 font-w700 tracking-givmo text-balance text-text-strong',
  successHeading: 'font-sans text-s22 font-w700 tracking-givmo text-balance text-success-600',
  body: 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary',
  textButton: TEXT_BUTTON,
  resendButton: (enabled: boolean) =>
    enabled
      ? TEXT_BUTTON
      : 'self-start font-sans text-s13 font-w600 tracking-givmo text-text-tertiary outline-none',
  inlineLink:
    'font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline',
  primary: { appearance: 'saas', tone: 'primary', size: 'lg', fullWidth: true },
  passwordRule: (met: boolean) =>
    `flex items-center gap-8 font-sans text-s13 tracking-givmo ${met ? 'text-success-600' : 'text-text-tertiary'}`,
  companyName: 'font-sans text-s16 font-w700 tracking-givmo text-text-strong',
} satisfies Omit<SignUpLook, 'choiceGroup' | 'choiceDivider' | 'terms'>;

/**
 * The employer portal — the register of its sign-in chooser (`SignInMainScreen`): its ways in
 * stacked 12 apart with "or continue with" between the email way and the providers, and its last line
 * centred under a hairline — balanced, so at phone width the sentence breaks into two even lines
 * rather than leaving "Privacy Policy." alone.
 */
export const PORTAL_SIGN_UP_LOOK: SignUpLook = {
  ...DOOR_REGISTER,
  choiceGroup: 'flex flex-col gap-12',
  choiceDivider: true,
  terms: `border-t border-border-subtle pt-20 text-center text-balance ${TERMS_TYPE}`,
};

/**
 * The company console — the register of its own sign-in door (`SignInPage`): its provider stack, and
 * one quiet line under the ways in.
 */
export const CONSOLE_SIGN_UP_LOOK: SignUpLook = {
  ...DOOR_REGISTER,
  choiceGroup: 'flex flex-col gap-10',
  choiceDivider: false,
  terms: TERMS_TYPE,
};

/** The words that name the host itself; every other word of the funnel is the same in both. */
export interface SignUpWords {
  /** The link, inside a sentence, to the host's sign-in door. */
  readonly signIn: string;
  /** The control that signs a mid-funnel account out. */
  readonly signOut: string;
  /**
   * What an approved teammate gets access to ("You’ll get access to {it} once…"), or `null` where no
   * one place says it: on the console a Global Admin chooses the apps at approval (D-EP-69), so the
   * sentence names none ("You’ll get access once…").
   */
  readonly accessedProduct: string | null;
  /** One of the people who approve a request to join the company ("{it} of Acme will approve you"). */
  readonly approver: string;
  /** All of them ("We’ve let {them} of Acme know…"). */
  readonly approvers: string;
}

/** The employer portal — today's words. */
export const PORTAL_SIGN_UP_WORDS: SignUpWords = {
  signIn: 'Log-in',
  signOut: 'Logout',
  accessedProduct: 'the portal',
  approver: 'an owner',
  approvers: 'the owners',
};

/**
 * The company console — its door's "Sign in", its shell's "Sign out", no one app as what they will
 * open (a Global Admin chooses the apps at approval, D-EP-69: D-EP-58's "the Donations admin app" no
 * longer holds), and the company-wide role that approves a join request by the name the console gives
 * it.
 */
export const CONSOLE_SIGN_UP_WORDS: SignUpWords = {
  signIn: 'Sign in',
  signOut: 'Sign out',
  accessedProduct: null,
  approver: `a ${GLOBAL_ADMIN_LABEL}`,
  approvers: `the ${GLOBAL_ADMIN_LABEL}s`,
};
